import React from 'react';
import { MdLan, MdMemory, MdSettingsApplications, MdStorage } from 'react-icons/md';

// Shared helpers and configuration for the device monitoring views. Statuses
// are decided by the backend (backend/app/services/device_health.py); this
// only presents them. See docs/architecture/monitoring.
const API = process.env.REACT_APP_API_URL;

export async function fetchJson(path, method = 'GET', body) {
  const response = await fetch(`${API}${path}`, {
    credentials: 'include', method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.error || data?.message || (response.status === 401 ? 'Your session expired. Sign in again.' : 'The request failed. Check your connection and try again.'));
  if (!data) throw new Error('The server returned an invalid response. Try again.');
  return data;
}

export const STATUS_LABELS = { ok: 'OK', warning: 'Warning', critical: 'Critical', unknown: 'Unknown', offline: 'Offline', no_data: 'No agent' };
const STATUS_ORDER = ['ok', 'unknown', 'warning', 'critical'];

export function tone(status) {
  return { ok: 'ok', warning: 'warning', critical: 'critical' }[status] || 'neutral';
}

export function worst(components) {
  return components.reduce((result, item) => (STATUS_ORDER.indexOf(item.status) > STATUS_ORDER.indexOf(result.status) ? item : result), components[0]);
}

export function StatusPill({ status, children }) {
  return <span className={`dm-pill dm-pill--${tone(status)}`}>{children || STATUS_LABELS[status] || status}</span>;
}

export function relativeTime(value, now = Date.now()) {
  if (!value) return 'Never';
  const seconds = Math.max(0, Math.round((now - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds} s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} h ago`;
  return `${Math.round(seconds / 86400)} d ago`;
}

export function duration(seconds) {
  if (seconds == null) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return days ? `${days} d ${hours} h` : hours ? `${hours} h` : `${Math.round(seconds / 60)} min`;
}

export function format(value, unit, digits = 0) {
  return typeof value === 'number' ? `${value.toFixed(digits)}${unit}` : '—';
}

const yesNo = value => (value === true ? 'Yes' : value === false ? 'No' : '—');

// Checks of the Pi itself (backend BOARD_RULES), always shown.
export const BOARD_CARDS = [
  { component: 'system', title: 'CPU and memory', icon: MdMemory, facts: ({ system = {}, throttle = {} }) => [
    ['CPU temperature', format(system.cpu_temp_c, ' °C', 1)], ['Load (1 min)', format(system.load_1m, '', 2)],
    ['RAM available', system.mem_total_mb ? `${system.mem_available_mb} / ${system.mem_total_mb} MB` : '—'],
    ['Throttled now', yesNo(throttle.now?.throttled || throttle.now?.frequency_capped)]] },
  { component: 'disk', title: 'Disk usage', icon: MdStorage, facts: ({ storage = {} }) => [
    ['Root used', storage.root_total_gb ? `${format(storage.root_used_percent, '%')} of ${storage.root_total_gb} GB` : '—'],
    ['SMART healthy', yesNo(storage.smart_ok)], ['Wear · temperature', `${format(storage.wear_percent, '%')} · ${format(storage.temp_c, ' °C')}`]] },
  { component: 'services', title: 'Services', icon: MdSettingsApplications, facts: ({ services = {} }) =>
    Object.entries(services).sort().map(([name, state]) => [name, state]) },
  { component: 'network', title: 'Network', icon: MdLan, facts: ({ network = {} }) => [
    ['Interface · Wi-Fi', `${network.interface || '—'} · ${format(network.signal_dbm, ' dBm')}`],
    ...[['Router', network.gateway_ping], ['Internet', network.internet_ping]].filter(([, ping]) => ping).map(([where, ping]) =>
      [`${where} (${ping.target})`, `${format(ping.avg_ms, ' ms', 1)} · jitter ${format(ping.jitter_ms, ' ms', 1)} · loss ${format(ping.loss_percent, '%')}`]),
    ['Backend round trip', format(network.last_latency_ms, ' ms')]] },
];

// What each check type measured, shown under its part (keys = backend/device probe names).
export const PROBE_FACTS = {
  pi_board: (d, report) => [['Model', d.model || '—'], ['RAM', format(d.ram_gb, ' GB')], ['Uptime', duration(report.system?.uptime_s)]],
  storage_device: d => [['Detected', d.present ? `${d.name || 'yes'} · ${format(d.size_gb, ' GB', 1)}` : 'No']],
  usb_audio: d => [['On USB', yesNo(d.usb)], ['Microphone input', d.source || '—'], ['App MICROPHONE_TYPE', d.app_microphone_type || '—']],
  audio_output: d => [['Output device', d.sink || '—']],
  hdmi_usb_touch: d => {
    const hdmi = (d.hdmi || []).find(item => item.status === 'connected') || (d.hdmi || [])[0];
    return [['Mode · EDID', hdmi ? `${hdmi.modes?.[0] || 'no mode'} · ${hdmi.edid_bytes ? `${hdmi.edid_bytes} bytes` : 'missing'}` : '—'],
      ['Backlight', d.backlight_on == null ? '—' : d.backlight_on ? 'On' : 'Off (screen asleep or off)']];
  },
  fan_tach: d => [['Fan speed', typeof d.rpm === 'number' ? `${d.rpm} rpm` : '—'],
    ['Target (pwm)', typeof d.pwm === 'number' ? `${d.pwm} / ${d.pwm_max || 255}` : '—'], ['Fan stage', d.stage != null ? `${d.stage} / ${d.max_stage ?? 4}` : '—']],
  pi5_power: (d, report) => [['Input voltage', format(d.input_v, ' V', 2)],
    ['Supply negotiated', d.psu_max_current_ma ? `${(d.psu_max_current_ma / 1000).toFixed(1)} A` : '—'],
    ['Board power (est.)', format(d.board_power_w_est, ' W', 1)],
    ['Undervoltage now · since boot', `${yesNo(report.throttle?.now?.under_voltage)} · ${yesNo(report.throttle?.since_boot?.under_voltage)}`]],
  loopback_camera: d => [['Camera sensor', (d.sensors || []).join(', ') || 'None'], ['Feed device', yesNo(d.video_device)],
    ['Feeder running', yesNo(d.feeder_active)]],
  uart_tof: d => [['App sensor (.env)', d.app_model || '—'], ['Last error', d.error || 'None']],
  i2c_tof: d => [['App sensor (.env)', d.app_model || '—'], ['Last error', d.error || 'None']],
  i2c_device: d => [['Answers on I2C', yesNo(d.present)]],
};

// Metrics selectable in the history chart (keys from backend METRICS).
export const CHART_METRICS = [
  ['cpu_temp_c', 'CPU °C', 1], ['fan_rpm', 'Fan rpm', 0], ['input_v', 'Input V', 2], ['board_power_w', 'Board W', 1],
  ['disk_used_percent', 'Disk %', 0], ['wifi_dbm', 'Wi-Fi dBm', 0], ['ping_ms', 'Ping ms', 0], ['jitter_ms', 'Jitter ms', 1],
  ['packet_loss_percent', 'Loss %', 0],
];

export function groupByCategory(items, category = item => item.category) {
  const groups = new Map();
  items.forEach(item => groups.set(category(item), [...(groups.get(category(item)) || []), item]));
  return [...groups.entries()];
}
